import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Phone,
  Check,
  Clock,
  Users,
  FileText,
  MapPin,
  Store,
  Factory,
  Boxes,
  Dumbbell,
  Music,
  Briefcase,
} from "lucide-react";
import Header from "@/components/sections/Header";
import Footer from "@/components/sections/Footer";
import CalcForm from "@/components/sections/CalcForm";
import { useSeo } from "@/lib/useSeo";
import { trackEvent } from "@/lib/analytics";

// Architectural shot of a clean modern warehouse/industrial interior —
// matches the same minimalist B2B feel as the other service pages.
const HERO_IMG =
  "https://images.unsplash.com/photo-1553413077-190dd305871c?auto=format&fit=crop&w=1920&q=85";

const INCLUDED = [
  "Mytí podlah (průmyslové plochy, dlažba, vinyl, beton)",
  "Čištění sociálních zařízení a toalet",
  "Úklid kuchyňky a zázemí pro zaměstnance",
  "Otírání ploch, pultů a regálů",
  "Čištění šaten a sprch",
  "Mytí oken zevnitř i zvenku (přízemní a nízkopodlažní objekty)",
  "Vynesení odpadu a třídění",
];

const TYPES = [
  { Icon: Store, title: "Obchody a prodejny" },
  { Icon: Factory, title: "Výrobní haly" },
  { Icon: Boxes, title: "Sklady a logistická centra" },
  { Icon: Dumbbell, title: "Sportovní centra a posilovny" },
  { Icon: Music, title: "Kulturní centra" },
  { Icon: Briefcase, title: "Kanceláře a open-space" },
];

const WHY = [
  {
    Icon: Clock,
    title: "Mimo provozní dobu",
    desc: "Uklízíme ráno, večer nebo o víkendu dle dohody. Chod provozovny nenarušíme.",
  },
  {
    Icon: FileText,
    title: "Fakturace firmám",
    desc: "Měsíční fakturace, splatnost na míru, vše hladce pro vaše účetní.",
  },
  {
    Icon: MapPin,
    title: "Lokální Brno tým",
    desc: "Sídlíme a pracujeme v Brně. Krátká reakční doba, žádné call-centrum.",
  },
];

const STEPS = [
  {
    step: "01",
    title: "Analýza prostor",
    desc: "Přijedeme, zaměříme plochy a zhodnotíme frekvenci a nároky provozu.",
  },
  {
    step: "02",
    title: "Návrh řešení",
    desc: "Připravíme harmonogram a cenovou kalkulaci šitou přesně na váš objekt.",
  },
  {
    step: "03",
    title: "Nasazení týmu",
    desc: "Prověřený tým začne pracovat podle schváleného standardu.",
  },
  {
    step: "04",
    title: "Kontrola kvality",
    desc: "Manažer pravidelně provádí audit a reaguje na vaši zpětnou vazbu.",
  },
];

const FAQ = [
  {
    q: "Jaká je cena úklidu provozovny v Brně?",
    a: "Cena se odvíjí od typu provozu, velikosti prostor a frekvence úklidu. Naše sazba začíná od 250 Kč/hodinu. Přesnou kalkulaci připravíme po bezplatné prohlídce — kontaktujte nás a ozveme se do 24 hodin.",
  },
  {
    q: "Uklízíte i mimo pracovní dobu?",
    a: "Ano, přizpůsobíme se vašemu provozu. Nabízíme ranní úklid před otevřením, úklid v průběhu dne i večerní úklid po skončení provozu.",
  },
  {
    q: "Jak rychle nastoupíte?",
    a: "Jsme schopni nastoupit zpravidla do 48 hodin od podpisu smlouvy.",
  },
  {
    q: "Myjete i okna?",
    a: "Ano, myjeme okna zevnitř i zvenku u přízemních a nízkopodlažních objektů.",
  },
  {
    q: "Uklízíte i v okolí Brna?",
    a: "Ano, působíme i v okolí Brna například v Modřicích a dalších obcích. V případě dotazu nás kontaktujte.",
  },
];

const UklidProvozovenBrno = () => {
  useSeo({
    title:
      "Úklid provozoven Brno | Komerční úklid hal a obchodů | SANVIORO",
    description:
      "Komerční úklid provozoven, výrobních hal, skladů, obchodů a sportovních center v Brně. Facility servis pro firmy. Nabídka do 24 hodin, sazba od 250 Kč/hod.",
    path: "/uklid-provozoven-brno",
  });

  // Inline JSON-LD for LocalBusiness + Service + FAQ
  const ldJson = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "LocalBusiness",
        "@id": "https://sanvioro.cz/#business",
        name: "SANVIORO",
        legalName: "Sanvioro s.r.o.",
        description:
          "Profesionální úklid provozoven, hal, skladů, obchodů a kanceláří v Brně a okolí.",
        telephone: "+420775601254",
        email: "info@sanvioro.cz",
        url: "https://sanvioro.cz/",
        areaServed: { "@type": "City", name: "Brno" },
        address: {
          "@type": "PostalAddress",
          streetAddress: "Příkop 843/4",
          addressLocality: "Brno",
          postalCode: "60200",
          addressCountry: "CZ",
        },
      },
      {
        "@type": "Service",
        name: "Úklid provozoven v Brně",
        provider: { "@id": "https://sanvioro.cz/#business" },
        areaServed: { "@type": "City", name: "Brno" },
        serviceType:
          "Pravidelný úklid provozoven, hal, skladů, obchodů a sportovních center",
        url: "https://sanvioro.cz/uklid-provozoven-brno",
      },
      {
        "@type": "FAQPage",
        mainEntity: FAQ.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  };

  return (
    <div
      className="App font-body text-[#111111] bg-white"
      data-testid="uklid-provozoven-page"
    >
      <Header />

      {/* JSON-LD structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(ldJson) }}
      />

      {/* HERO */}
      <section className="relative min-h-[88svh] overflow-hidden bg-[#F5F5F7]">
        <div className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt="Provozovna a výrobní hala v Brně po profesionálním úklidu SANVIORO"
            className="w-full h-full object-cover"
            style={{ filter: "brightness(1.18) contrast(0.92) saturate(0.95)" }}
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(to bottom, rgba(46,52,60,0.42) 0%, rgba(46,52,60,0.28) 45%, rgba(30,36,44,0.62) 100%)",
            }}
          />
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "radial-gradient(ellipse at 78% 18%, rgba(27,147,164,0.18) 0%, rgba(27,147,164,0) 55%)",
            }}
          />
        </div>
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.08]"
          style={{
            backgroundImage:
              "linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)",
            backgroundSize: "80px 80px",
          }}
        />

        <div className="relative mx-auto max-w-[1400px] px-6 md:px-12 lg:px-20 pt-32 md:pt-40 pb-16 md:pb-24 flex flex-col">
          <div className="text-white/85 text-xs uppercase tracking-[0.28em] font-semibold">
            Brno · úklid hal, skladů a obchodů
          </div>

          <h1
            data-testid="page-h1"
            className="mt-10 max-w-4xl font-display font-black text-white text-[clamp(2.5rem,7vw,6rem)] leading-[0.95] tracking-[-0.03em]"
          >
            Úklid provozoven
            <span className="block italic text-[#1B93A4]">v Brně.</span>
          </h1>

          <p className="mt-8 max-w-2xl text-white text-[18px] md:text-[22px] leading-[1.75] font-light">
            Pravidelný i jednorázový úklid obchodů, výrobních hal, skladů,
            sportovních a kulturních center. Přizpůsobíme se vašemu provozu.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a
              href="#kalkulace"
              data-testid="hero-cta"
              onClick={() =>
                trackEvent("cta_click_spocitat_uklid", {
                  location: "uklid_provozoven_hero",
                })
              }
              className="group inline-flex items-center gap-3 bg-white text-[#111111] px-7 h-14 font-semibold tracking-tight hover:bg-[#0F5C69] hover:text-white transition-colors"
            >
              Získat nabídku
              <ArrowRight
                size={18}
                strokeWidth={2.5}
                className="transition-transform group-hover:translate-x-1"
              />
            </a>
            <a
              href="tel:+420775601254"
              className="inline-flex items-center gap-3 bg-transparent text-white border border-white/40 px-7 h-14 font-semibold tracking-tight hover:bg-white/10 transition-colors"
            >
              <Phone size={16} strokeWidth={2.5} />
              <span className="tabular-nums">+420 775 601 254</span>
            </a>
          </div>
        </div>
      </section>

      {/* CO ZAHRNUJE */}
      <section className="bg-white py-16 md:py-20">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-20">
            <div className="lg:col-span-5">
              <div className="text-xs uppercase tracking-[0.28em] font-bold text-[#0F5C69]">
                [ 01 ] — Co zahrnuje
              </div>
              <h2 className="font-display text-4xl md:text-6xl font-bold tracking-[-0.025em] leading-[1.02] mt-5">
                Co je<br />součástí úklidu.
              </h2>
              <p className="mt-8 text-[#555555] leading-relaxed text-base md:text-lg max-w-md">
                Standardní rozsah pro provozovny. Specifické požadavky
                domlouváme individuálně.
              </p>
            </div>
            <div className="lg:col-span-7 lg:pl-8 lg:border-l border-[#E5E5E5]">
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                {INCLUDED.map((it) => (
                  <li
                    key={it}
                    className="flex items-start gap-3 text-[15px] text-[#222222] leading-relaxed"
                  >
                    <Check
                      size={18}
                      strokeWidth={2.5}
                      className="text-[#0F5C69] shrink-0 mt-1"
                    />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* TYPY PROVOZOVEN */}
      <section className="bg-[#F5F5F7] py-16 md:py-20">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-20">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.28em] font-bold text-[#0F5C69]">
              [ 02 ] — Typy provozoven
            </div>
            <h2 className="font-display text-4xl md:text-6xl font-bold tracking-[-0.025em] leading-[1.02] mt-5">
              Kde<br />uklízíme.
            </h2>
          </div>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E5E5E5] border border-[#E5E5E5]">
            {TYPES.map((t, i) => (
              <div
                key={t.title}
                data-testid={`type-card-${i}`}
                className="bg-white p-8 md:p-10 min-h-[180px] flex flex-col justify-between"
              >
                <div className="w-12 h-12 border border-[#0F5C69] flex items-center justify-center">
                  <t.Icon
                    size={20}
                    strokeWidth={1.5}
                    className="text-[#0F5C69]"
                  />
                </div>
                <h3 className="font-display text-xl md:text-2xl font-bold tracking-tight mt-6 text-[#111111]">
                  {t.title}
                </h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROČ SANVIORO */}
      <section className="bg-white py-16 md:py-20">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-20">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.28em] font-bold text-[#0F5C69]">
              [ 03 ] — Proč my
            </div>
            <h2 className="font-display text-4xl md:text-6xl font-bold tracking-[-0.025em] leading-[1.02] mt-5">
              3 důvody, proč<br />vsadit na SANVIORO.
            </h2>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E5E5E5] border border-[#E5E5E5]">
            {WHY.map((w, i) => (
              <div
                key={w.title}
                data-testid={`why-card-${i}`}
                className="bg-white p-8 md:p-10 min-h-[260px] flex flex-col"
              >
                <div className="w-12 h-12 rounded-xl bg-[#0F5C69] flex items-center justify-center shadow-sm">
                  <w.Icon
                    size={22}
                    strokeWidth={2}
                    className="text-white"
                  />
                </div>
                <h3 className="font-display text-2xl font-bold tracking-tight mt-8">
                  {w.title}
                </h3>
                <p className="mt-3 text-[#555555] leading-relaxed text-[15px]">
                  {w.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* JAK TO FUNGUJE */}
      <section className="bg-[#F5F5F7] py-16 md:py-20">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-20">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.28em] font-bold text-[#0F5C69]">
              [ 04 ] — Proces
            </div>
            <h2 className="font-display text-4xl md:text-6xl font-bold tracking-[-0.025em] leading-[1.02] mt-5">
              Jak to<br />funguje.
            </h2>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {STEPS.map((s, i) => (
              <div
                key={s.step}
                data-testid={`process-step-${s.step}`}
                className="relative border-t border-[#E5E5E5] pt-8"
              >
                <div className="absolute -top-px left-0 w-12 h-px bg-[#0F5C69]" />
                <div className="font-display text-5xl md:text-6xl font-black text-[#E6F2F4] leading-none tracking-tight">
                  {s.step}
                </div>
                <h3 className="font-display text-xl md:text-2xl font-bold tracking-tight mt-6 text-[#111111]">
                  {s.title}
                </h3>
                <p className="mt-3 text-[#555555] leading-relaxed text-[15px]">
                  {s.desc}
                </p>
                {i < STEPS.length - 1 && (
                  <div className="hidden lg:block absolute top-12 -right-4 text-[#0F5C69]/40">
                    <ArrowRight size={18} strokeWidth={2} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* KALKULACE — re-uses CalcForm; trim bottom padding so FAQ flows directly */}
      <div id="kalkulace" className="-mb-12 md:-mb-16">
        <CalcForm />
      </div>

      {/* FAQ */}
      <section className="bg-white pt-4 md:pt-6 pb-16 md:pb-20">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <div className="text-xs uppercase tracking-[0.28em] font-bold text-[#0F5C69]">
                [ 05 ] — Časté otázky
              </div>
              <h2 className="font-display text-4xl md:text-6xl font-bold tracking-[-0.025em] leading-[1.02] mt-5">
                Co se nás<br />klienti ptají.
              </h2>
              <p className="mt-8 text-[#555555] leading-relaxed max-w-md">
                Něco nenacházíte? Napište nám na{" "}
                <a
                  href="mailto:info@sanvioro.cz"
                  className="text-[#0F5C69] underline decoration-[#0F5C69]/40 underline-offset-4 hover:decoration-[#0F5C69]"
                >
                  info@sanvioro.cz
                </a>{" "}
                — odpovíme do 24 hodin.
              </p>
            </div>
            <div className="lg:col-span-7">
              <div className="divide-y divide-[#E5E5E5] border-y border-[#E5E5E5]">
                {FAQ.map((f, i) => (
                  <details
                    key={f.q}
                    data-testid={`faq-${i}`}
                    className="group py-6 cursor-pointer"
                  >
                    <summary className="flex justify-between items-start gap-4 list-none">
                      <span className="font-display text-lg md:text-xl font-semibold tracking-tight text-[#111111]">
                        {f.q}
                      </span>
                      <span className="shrink-0 text-2xl font-light text-[#0F5C69] transition-transform group-open:rotate-45">
                        +
                      </span>
                    </summary>
                    <p className="mt-4 text-[#555555] leading-relaxed text-[15px] pr-8">
                      {f.a}
                    </p>
                  </details>
                ))}
              </div>
              <div className="mt-8">
                <a
                  data-testid="reviews-google-link"
                  href="https://g.page/r/CX0iCsN9Py7SEAE/review"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() =>
                    trackEvent("cta_click_google_reviews", {
                      location: "uklid_provozoven_faq",
                    })
                  }
                  className="group inline-flex items-center gap-2 font-display text-lg md:text-xl font-semibold tracking-tight text-[#0F5C69] hover:gap-3 transition-all"
                >
                  Přečtěte si, co říkají naši klienti na Google
                  <ArrowRight
                    size={18}
                    strokeWidth={2.5}
                    className="transition-transform group-hover:translate-x-1"
                  />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA NA KONCI */}
      <section
        data-testid="final-cta-section"
        className="bg-[#0F5C69] text-white py-16 md:py-20"
      >
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
            <div className="lg:col-span-7">
              <div className="text-xs uppercase tracking-[0.28em] font-bold text-white/70">
                Začněme spolupráci
              </div>
              <h2 className="font-display text-4xl md:text-6xl font-bold tracking-[-0.025em] leading-[1.02] mt-5">
                Připraveni začít?
              </h2>
              <p className="mt-8 text-white/85 leading-relaxed text-base md:text-lg max-w-xl">
                Ozvěte se — do 24 hodin se vám ozve náš manažer, domluvíme
                nezávaznou prohlídku a připravíme kalkulaci šitou na míru.
              </p>
            </div>
            <div className="lg:col-span-5 flex flex-wrap gap-4">
              <a
                href="#kalkulace"
                data-testid="final-cta-primary"
                onClick={() =>
                  trackEvent("cta_click_spocitat_uklid", {
                    location: "uklid_provozoven_final",
                  })
                }
                className="group inline-flex items-center gap-3 bg-white text-[#0F5C69] px-7 h-14 font-semibold tracking-tight hover:bg-[#E6F2F4] transition-colors"
              >
                Získat nabídku
                <ArrowRight
                  size={18}
                  strokeWidth={2.5}
                  className="transition-transform group-hover:translate-x-1"
                />
              </a>
              <a
                href="tel:+420775601254"
                data-testid="final-cta-phone"
                onClick={() =>
                  trackEvent("cta_click_phone", {
                    location: "uklid_provozoven_final",
                  })
                }
                className="inline-flex items-center gap-3 bg-transparent text-white border border-white/40 px-7 h-14 font-semibold tracking-tight hover:bg-white/10 transition-colors"
              >
                <Phone size={16} strokeWidth={2.5} />
                <span className="tabular-nums">+420 775 601 254</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* INTERNÍ PROLINKOVÁNÍ */}
      <section className="bg-[#F5F5F7] py-12 md:py-16">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-20 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
          <div>
            <div className="text-xs uppercase tracking-[0.28em] font-bold text-[#0F5C69]">
              Další služby SANVIORO
            </div>
            <h3 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-3 max-w-xl text-[#111111]">
              Uklízíme i kanceláře a bytové domy.
            </h3>
            <p className="mt-4 text-[#555555] max-w-xl leading-relaxed">
              Hledáte úklid kanceláří v Brně nebo úklid společných prostor
              bytového domu? Podívejte se na další služby pro firmy a SVJ.
            </p>
          </div>
          <div className="flex flex-wrap gap-4 shrink-0">
            <Link
              to="/uklid-kancelari-brno"
              className="inline-flex items-center gap-2 bg-[#0F5C69] text-white px-6 h-12 font-semibold hover:bg-[#0B4B55] transition-colors"
            >
              Úklid kanceláří v Brně
            </Link>
            <Link
              to="/uklid-bytovych-domu-brno"
              className="inline-flex items-center gap-2 border border-[#0F5C69]/30 text-[#0F5C69] px-6 h-12 font-semibold hover:bg-[#E6F2F4] transition-colors"
            >
              Úklid bytových domů
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default UklidProvozovenBrno;
